<script lang="ts" setup>
import { ref, reactive, computed, watch, onMounted } from "vue";
import { ElNotification, UploadProps } from "element-plus";
import type { UploadFile } from "element-plus";
import { Close, DocumentAdd } from "@element-plus/icons-vue";
import { ElementUIFormRef } from "~/types/element-ui/ElementUIFormRef";
import { FormRuleType } from "~/types/element-ui/FormRuleType";
import { Profile } from "~/types/profile";
import { ProfileCustomField } from "~/types/profileCustomFields";
import { PermissionNames } from "~/configs/permission_names";
import { useOrganizationStore } from "~/projects/crm/stores/organizationStore";
import { useSettingsStore } from "~/stores/settingsStore";
import { useConfigStore } from "~/stores/configStore";
import { useCallGroupStore } from "~/projects/cc/stores/callGroupsStore";
import AttachmentForm from "~/components/AttachmentForm.vue"; // path as per your structure
import { useAttachmentsStore } from "~/stores/attachmentsStore";
import { useAuthStore } from "~/stores/auth";

const profileStore = useCustomerProfileStore();
const organizationStore = useOrganizationStore();
const settingsStore = useSettingsStore();
const configStore = useConfigStore();
const callGroupStore = useCallGroupStore();
const authStore = useAuthStore();

const activeTab = ref("basic");

const formRef = ref<ElementUIFormRef | null>(null);
const fileList = ref<UploadFile[]>([]);

const showViewer = ref(false);
const previewIndex = ref(0);

const showAttachmentModal = ref(false);
const attachmentArray = ref([]);

const props = defineProps({
  showEditModalHeader: {
    type: Boolean,
    required: false,
    default: true,
  },
  isFromLead: {
    type: Boolean,
    required: false,
    default: false,
  },
  isFromOpp: {
    type: Boolean,
    default: false,
  },
  orgId: {
    type: Number,
    required: false,
    default: null,
  },
});

const emit = defineEmits(["created"]);

onMounted(async () => {
  await organizationStore.getSimpleOrganizations();
  await settingsStore.getProfileCustomFields();
  await callGroupStore.getCallGroups();
  if (props.orgId) {
    formData.organization_id = props.orgId;
  }
});

const formData = reactive({
  ...({} as Partial<Profile>),

  phones: [],
  emails: [],
  addresses: [],
  custom_fields: {} as Record<string, any>,
  assigned_call_group_id: null,
  assigned_call_agent_id: null,
  ticket_agent_id: null,
});

// An inbox-only agent (no ticket-show, no profile-show-others) can only ever own
// the tickets on the profiles they create, so the picker is locked to them
// rather than left open to every agent in the org.
const ticketAgentLocked = computed(
  () =>
    !isUserPermitted(PermissionNames.PROFILE_SHOW_OTHERS) &&
    !isUserPermitted(PermissionNames.TICKET_SHOW) &&
    isUserPermitted(PermissionNames.TICKET_INBOX_SHOW),
);

const ownTicketAgentId = computed(() => authStore.user?.id ?? null);

// Permissions and the auth user resolve asynchronously, so keep the field in
// sync instead of seeding it once at setup time.
watch(
  [ticketAgentLocked, ownTicketAgentId],
  ([locked, ownId]) => {
    if (locked) {
      formData.ticket_agent_id = ownId;
    }
  },
  { immediate: true },
);

// Flat list of profile custom field definitions coming from /settings.
const customFields = computed<ProfileCustomField[]>(
  () => settingsStore.profileCustomFields || [],
);

// Values are stored under `custom_fields`, keyed by slug, to match the backend
// payload. Ensure the object (and every slug) exists so v-model has a reactive
// target, defaulting multi-select fields to an array.
const ensureCustomFields = () => {
  if (!formData.custom_fields) {
    formData.custom_fields = {};
  }
  for (const field of customFields.value) {
    if (formData.custom_fields[field.slug] === undefined) {
      formData.custom_fields[field.slug] =
        field.type === "multi_select" ? [] : null;
    }
  }
};
watch(customFields, ensureCustomFields, { immediate: true });

const rules = reactive<FormRuleType>({
  organization_id: [
    {
      required: false,
      trigger: "change",
    },
  ],
  name: [
    {
      required: true,
      message: "name is required",
      trigger: "blur",
    },
  ],
  middle_name: [
    {
      required: false,
    },
  ],
  last_name: [
    {
      required: false,
    },
  ],
});

const handleAddAttachment = (attachment: any) => {
  attachmentArray.value.push(attachment);
};

const removeAttachment = (index: number) => {
  attachmentArray.value.splice(index, 1);
};

const createProfile = async () => {
  const multipartFormData = new FormData();

  let source = "manual";

  if (props.isFromLead) {
    source = "lead_manual";
  } else if (props.isFromOpp) {
    source = "opportunity_manual";
  }

  if (!formRef.value) return;

  await formRef.value.validate(async (valid: boolean) => {
    if (!valid) {
      ElNotification({
        title: "Failed",
        message: "Please provide all the required fields",
        type: "error",
      });
      return;
    }
    for (const [key, value] of Object.entries(formData)) {
      if (value !== null) {
        if (key === "custom_fields") {
          // Custom field values are stored keyed by slug. The backend expects
          // them flattened as custom_fields[slug] (and [] for multi-selects).
          Object.entries(value as Record<string, any>).forEach(
            ([slug, fieldValue]) => {
              if (fieldValue === null || fieldValue === undefined) return;

              if (Array.isArray(fieldValue)) {
                fieldValue.forEach((item) => {
                  multipartFormData.append(`${key}[${slug}][]`, item);
                });
              } else {
                const formattedValue =
                  typeof fieldValue === "boolean"
                    ? fieldValue
                      ? 1
                      : 0
                    : fieldValue;
                multipartFormData.set(`${key}[${slug}]`, formattedValue as any);
              }
            },
          );
        } else if (Array.isArray(value)) {
          value.forEach((item, index) => {
            if (typeof item === "object" && item !== null) {
              Object.entries(item).forEach(([nestedKey, nestedValue]) => {
                let formattedValue = nestedValue;
                if (typeof nestedValue === "boolean") {
                  formattedValue = nestedValue ? 1 : 0;
                }
                multipartFormData.append(
                  `${key}[${index}][${nestedKey}]`,
                  formattedValue,
                );
              });
            } else {
              multipartFormData.append(`${key}[]`, item);
            }
          });
        } else {
          multipartFormData.set(key, value as any);
        }
      }
    }

    multipartFormData.set("source", source);

    const attachments = fileList.value.map((file) => ({
      file: file.raw,
      name: file.attachmentName,
      description: file.description || "",
    }));

    const profile = await profileStore.createProfile(
      multipartFormData,
      attachments,
    );
    console.log(multipartFormData, "multipartFormData");
    if (profile) {
      ElNotification({
        title: "Profile Created",
        message: "New profile created successfully",
        type: "success",
      });
      await profileStore.getProfiles();
      profileStore.getSimpleProfiles();
      emit("created", profile);
      profileStore.toggleProfileCreateModal();
      profileStore.toggleProfileCreateDrawer();
    }
  });
};

const makeDefault = (index, propName) => {
  formData[propName].forEach((item, i) => {
    item.is_default = i === index;
  });
};
const handleFileChange: UploadProps["onChange"] = (uploadFile) => {
  const exists = fileList.value.find((file) => file.uid === uploadFile.uid);

  if (!uploadFile.url && uploadFile.raw?.type.startsWith("image")) {
    uploadFile.url = URL.createObjectURL(uploadFile.raw);
  }
  if (!uploadFile.attachmentName) {
    uploadFile.attachmentName = uploadFile.name
      .replace(/\.[^/.]+$/, "")
      .split(" ")[0];
  }

  if (!exists) {
    fileList.value.push({
      ...uploadFile,
      attachmentName: uploadFile.name,
      description: "",
    });
  }
};
const removeFile = (index: number) => {
  const file = fileList.value[index];
  if (file.url) {
    URL.revokeObjectURL(file.url);
  } else if (file.raw) {
    URL.revokeObjectURL(URL.createObjectURL(file.raw));
  }
  fileList.value.splice(index, 1);
};
const getAcceptValue = () => {
  const accept =
    ".jpg, .jpeg, .png, .pdf,.ppt, .pptx, .xlx,.xlxs, .doc, .docx, .wav, .heic, .mp3, .mp4";
  return accept;
};
const handlePreview: UploadProps["onPreview"] = (file) => {
  const exists = fileList.value.find((f) => f.uid === file.uid);
  if (!exists) {
    if (!file.url && file.raw?.type.startsWith("image")) {
      file.url = URL.createObjectURL(file.raw);
    }
    fileList.value.push(file);
  }
};
</script>
<template>
  <EditModalHeader v-if="props.showEditModalHeader">
    <template #title> Create Profile</template>
    <template #svg>
      <Svgs name="pen" />
    </template>
  </EditModalHeader>
  <el-form
    ref="formRef"
    :model="formData as Profile"
    :rules="rules as FormRuleType"
    class="space-y-5"
    @submit.prevent="createProfile"
  >
    <div class="feature-filter-grid-1">
      <el-tabs v-model="activeTab" type="card">
        <!-- Basic Tab -->
        <el-tab-pane label="Basic" name="basic">
          <div class="flex flex-col gap-4">
            <el-form-item class="form-elem-container" prop="name">
              <div class="form-elem-container">
                <FormLabel :required="true">Name</FormLabel>
                <el-input v-model="formData.name" placeholder="Enter name" />
              </div>
            </el-form-item>
            <el-form-item class="form-elem-container" prop="occupation">
              <div class="form-elem-container">
                <FormLabel>Occupation</FormLabel>
                <el-input
                  v-model="formData.occupation"
                  placeholder="Enter occupation"
                />
              </div>
            </el-form-item>
            <el-form-item class="form-elem-container" prop="organization_id">
              <div class="form-elem-container">
                <FormLabel> Organization</FormLabel>
                <el-select
                  v-model="formData.organization_id"
                  clearable
                  filterable
                  placeholder="Select organization"
                >
                  <el-option
                    v-for="organization in organizationStore.simpleOrganizations"
                    :key="organization.id"
                    :label="organization.name"
                    :value="organization.id"
                  ></el-option>
                </el-select>
                <OrganizationsAddButton />
              </div>
            </el-form-item>
            <el-form-item class="form-elem-container">
              <div class="form-elem-container">
                <FormLabel> Phones:</FormLabel>
                <hr class="border-t border-light-sec-purple border-dotted" />
                <FormDynElemCont class="flex flex-col gap-1">
                  <div
                    v-for="(phone, index) in formData.phones"
                    :key="index"
                    class="form-elem-container"
                  >
                    <FormLabel v-if="phone.is_default">Default Phone</FormLabel>
                    <FormLabel v-else>Additional Phone</FormLabel>
                    <FormDynElem @remove="formData.phones.splice(index, 1)">
                      <el-input v-model="phone.phone" type="number" clearable />
                      <CreateButton
                        v-if="!phone.is_default"
                        @click.prevent="makeDefault(index, 'phones')"
                      >
                        Default
                      </CreateButton>
                    </FormDynElem>
                  </div>
                  <FormElemAddButton
                    @add="
                      formData.phones.push(
                        formData.phones.length === 0
                          ? { is_default: true, phone: '' }
                          : { is_default: false, phone: '' },
                      )
                    "
                  >
                    Add new number
                  </FormElemAddButton>
                </FormDynElemCont>
              </div>
            </el-form-item>

            <el-form-item class="form-elem-container">
              <div class="form-elem-container">
                <FormLabel> Emails:</FormLabel>
                <hr class="border-t border-light-sec-purple border-dotted" />
                <FormDynElemCont class="flex flex-col gap-1">
                  <div
                    v-for="(email, index) in formData.emails"
                    :key="index"
                    class="form-elem-container"
                  >
                    <FormLabel v-if="email.is_default">Default Email</FormLabel>
                    <FormLabel v-else>Additional Email</FormLabel>
                    <FormDynElem @remove="formData.emails.splice(index, 1)">
                      <el-input v-model="email.email" clearable />
                      <CreateButton
                        v-if="!email.is_default"
                        @click.prevent="makeDefault(index, 'emails')"
                      >
                        Default
                      </CreateButton>
                    </FormDynElem>
                  </div>
                  <FormElemAddButton
                    @add="
                      formData.emails.push(
                        formData.emails.length === 0
                          ? { is_default: true, email: '' }
                          : { is_default: false, email: '' },
                      )
                    "
                  >
                    Add new Email
                  </FormElemAddButton>
                </FormDynElemCont>
              </div>
            </el-form-item>

            <el-form-item class="form-elem-container">
              <div class="form-elem-container">
                <FormLabel> Addresses:</FormLabel>
                <hr class="border-t border-light-sec-purple border-dotted" />
                <FormDynElemCont>
                  <div
                    v-for="(address, index) in formData.addresses"
                    :key="index"
                    class="form-elem-container"
                  >
                    <div class="grid grid-cols-2 gap-2">
                      <el-form-item class="form-elem-container" prop="country">
                        <FormLabel> Country </FormLabel>
                        <el-input
                          v-model="address.country"
                          placeholder="Enter Country"
                        />
                      </el-form-item>
                      <el-form-item class="form-elem-container" prop="state">
                        <FormLabel> District / State </FormLabel>
                        <el-input
                          v-model="address.state"
                          placeholder="Enter State"
                        />
                      </el-form-item>
                      <el-form-item class="form-elem-container" prop="zip_code">
                        <FormLabel> Postcode / ZIP </FormLabel>
                        <el-input
                          v-model="address.zip_code"
                          placeholder="Enter ZIP"
                        />
                      </el-form-item>
                      <el-form-item class="form-elem-container" prop="street">
                        <FormLabel> Street </FormLabel>
                        <el-input
                          v-model="address.street"
                          type="textarea"
                          :rows="2"
                          placeholder="Enter street address"
                        />
                      </el-form-item>
                    </div>
                    <div class="flex items-center gap-2">
                      <CreateButton
                        v-if="!address.is_default"
                        @click.prevent="makeDefault(index, 'addresses')"
                      >
                        Default
                      </CreateButton>
                      <Svgs
                        name="round-close"
                        stroke-width="1.2"
                        class="w-6 h-6 text-light-sec-purple cursor-pointer"
                        @click="formData.addresses.splice(index, 1)"
                      />
                    </div>
                  </div>
                  <FormElemAddButton
                    @add="
                      formData.addresses.push(
                        formData.addresses.length === 0
                          ? {
                              is_default: true,
                              country: '',
                              state: '',
                              zip_code: '',
                              street: '',
                            }
                          : {
                              is_default: false,
                              country: '',
                              state: '',
                              zip_code: '',
                              street: '',
                            },
                      )
                    "
                  >
                    Add new address
                  </FormElemAddButton>
                </FormDynElemCont>
              </div>
            </el-form-item>

            <el-form-item prop="attachments">
              <div class="flex flex-col gap-4">
                <div class="flex flex-col md:flex-row md:items-center gap-2">
                  <FormLabel>Attachments</FormLabel>
                  <div class="ml-2 mb-2">
                    <el-button
                      size="small"
                      plain
                      @click="
                        $refs.uploadRef?.$el.querySelector('input').click()
                      "
                    >
                      <DocumentAdd class="w-4 h-4" />
                      <p>Add Attachments</p>
                    </el-button>
                  </div>
                </div>

                <el-upload
                  ref="uploadRef"
                  v-model:file-list="fileList"
                  :auto-upload="false"
                  :multiple="true"
                  :show-file-list="false"
                  :on-preview="handlePreview"
                  :on-change="handleFileChange"
                  :accept="getAcceptValue()"
                  style="display: none"
                />

                <div class="grid grid-cols-1 md:grid-cols-2 gap-2">
                  <div
                    v-for="(file, index) in fileList"
                    :key="file.uid"
                    class="flex items-center gap-4 border p-2 rounded-md"
                  >
                    <div
                      class="w-16 h-16 flex items-center justify-center bg-gray-100 rounded"
                    >
                      <img
                        v-if="file.url || file.raw?.type.startsWith('image')"
                        :src="file.url"
                        class="w-full h-full object-cover rounded cursor-pointer"
                        @click="
                          () => {
                            previewIndex = index;
                            showViewer = true;
                          }
                        "
                      />
                      <el-icon v-else-if="file.raw?.type?.startsWith('audio')"
                        ><Headset
                      /></el-icon>
                      <el-icon v-else-if="file.raw?.type?.startsWith('video')"
                        ><VideoCamera
                      /></el-icon>
                      <el-icon v-else-if="file.raw?.type === 'application/pdf'"
                        ><Document
                      /></el-icon>
                      <el-icon v-else><Folder /></el-icon>
                    </div>

                    <div class="flex flex-col gap-1">
                      <el-input
                        v-model="file.attachmentName"
                        placeholder="File name"
                        class="w-1/3"
                      />
                      <el-input
                        v-model="file.description"
                        placeholder="File description"
                        type="textarea"
                      />
                    </div>

                    <el-button circle size="small" @click="removeFile(index)">
                      <el-icon><Close /></el-icon>
                    </el-button>
                  </div>
                </div>
              </div>
            </el-form-item>
          </div>
        </el-tab-pane>

        <!-- Additional Tab -->
        <el-tab-pane label="Additional" name="additional">
          <div class="grid grid-cols-2 gap-2">
            <el-form-item
              v-for="field of customFields"
              :key="field.slug"
              class="flex gap-3"
            >
              <div class="form-elem-container">
                <FormLabel :required="field.is_required">{{
                  field.label
                }}</FormLabel>
                <el-select
                  v-if="field.type === 'select'"
                  v-model="formData.custom_fields[field.slug]"
                  placeholder="Select"
                  filterable
                  clearable
                >
                  <el-option
                    v-for="item in field.options"
                    :key="item.id"
                    :label="item.label"
                    :value="item.id"
                  />
                </el-select>
                <el-select
                  v-else-if="field.type === 'multi_select'"
                  v-model="formData.custom_fields[field.slug]"
                  placeholder="Select"
                  multiple
                  :collapse-tags="true"
                  filterable
                  clearable
                >
                  <el-option
                    v-for="item in field.options"
                    :key="item.id"
                    :label="item.label"
                    :value="item.id"
                  />
                </el-select>
                <el-input
                  v-else-if="field.type === 'text'"
                  v-model="formData.custom_fields[field.slug]"
                  type="textarea"
                  :rows="2"
                />
                <el-input
                  v-else-if="field.type === 'string'"
                  v-model="formData.custom_fields[field.slug]"
                  type="text"
                />
                <el-input
                  v-else-if="field.type === 'int'"
                  v-model="formData.custom_fields[field.slug]"
                  type="number"
                />
                <el-input-number
                  v-else-if="field.type === 'decimal'"
                  v-model="formData.custom_fields[field.slug]"
                  :precision="2"
                  :step="0.01"
                  :controls="false"
                />
                <el-switch
                  v-else-if="field.type === 'boolean'"
                  v-model="formData.custom_fields[field.slug]"
                />
                <el-date-picker
                  v-else-if="field.type === 'date'"
                  v-model="formData.custom_fields[field.slug]"
                  placeholder="Pick a date"
                  type="date"
                  format="YYYY-MM-DD"
                  value-format="YYYY-MM-DD"
                />
                <el-date-picker
                  v-else-if="field.type === 'date_time'"
                  v-model="formData.custom_fields[field.slug]"
                  placeholder="Pick Date and Time"
                  type="datetime"
                  format="YYYY-MM-DD HH:mm:ss"
                  value-format="YYYY-MM-DD HH:mm:ss"
                />
              </div>
            </el-form-item>
          </div>
        </el-tab-pane>

        <!-- Assign Tab -->
        <el-tab-pane label="Assign" name="assign">
          <div class="flex flex-col gap-4">
            <fieldset
              class="min-w-0 rounded-md border border-light-purple-border dark:border-dark-bluegray-border px-3 pt-1 pb-3"
            >
              <legend class="assign-legend">Call</legend>

              <div class="grid grid-cols-2 gap-3">
                <el-form-item
                  class="form-elem-container !mb-0"
                  prop="assigned_call_group_id"
                >
                  <div class="form-elem-container">
                    <FormLabel>Group</FormLabel>
                    <el-select
                      v-model="formData.assigned_call_group_id"
                      filterable
                      clearable
                      placeholder="Select Group"
                      class="w-full"
                    >
                      <el-option
                        v-for="group in callGroupStore.tableData"
                        :key="group.id"
                        :label="group.group_name"
                        :value="group.id"
                      />
                    </el-select>
                  </div>
                </el-form-item>

                <el-form-item
                  class="form-elem-container !mb-0"
                  prop="assigned_call_agent_id"
                >
                  <div class="form-elem-container">
                    <FormLabel>Agent</FormLabel>
                    <el-select
                      v-model="formData.assigned_call_agent_id"
                      placeholder="Select Agent"
                      filterable
                      clearable
                      class="w-full"
                    >
                      <el-option
                        v-for="agent in configStore.globalConfig.agents"
                        :key="agent.id"
                        :label="agent.name"
                        :value="agent.id"
                      />
                    </el-select>
                  </div>
                </el-form-item>
              </div>
            </fieldset>

            <fieldset
              class="min-w-0 rounded-md border border-light-purple-border dark:border-dark-bluegray-border px-3 pt-1 pb-3"
            >
              <legend class="assign-legend">Ticket</legend>

              <!-- Same two-column grid as Call above, so the one field lines up
                   with Group rather than running the full width on its own. -->
              <div class="grid grid-cols-2 gap-3">
                <el-form-item
                  class="form-elem-container !mb-0"
                  prop="ticket_agent_id"
                >
                  <div class="form-elem-container">
                    <FormLabel>Agent</FormLabel>
                    <el-select
                      v-model="formData.ticket_agent_id"
                      placeholder="Select Agent"
                      filterable
                      class="w-full"
                      :clearable="!ticketAgentLocked"
                      :disabled="ticketAgentLocked"
                      :title="
                        ticketAgentLocked
                          ? 'Tickets on profiles you create are assigned to you'
                          : undefined
                      "
                    >
                      <el-option
                        v-for="agent in configStore.globalConfig.agents"
                        :key="agent.id"
                        :label="agent.name"
                        :value="agent.id"
                      />
                    </el-select>
                  </div>
                </el-form-item>
              </div>
            </fieldset>
          </div>
        </el-tab-pane>
      </el-tabs>
    </div>
    <div class="feature-filter-submit">
      <NavButton @click="profileStore.toggleProfileCreateModal()">
        Cancel
      </NavButton>
      <NavButton
        class="bg-light-purple-nav"
        type="submit"
        :is-disabled="!isUserPermitted(PermissionNames.PROFILE_CREATE)"
      >
        Create
      </NavButton>
    </div>
  </el-form>

  <!-- <AttachmentForm
    v-if="showAttachmentModal"
    :attachable-type="'profile'"
    :attachable-id="formData.id"
    :show-attachment-modal="showAttachmentModal"
    @add-attachment="handleAddAttachment"
    @close-attachment-modal="showAttachmentModal = false"
  /> -->
</template>

<style setup>
.custom-basic-tabs .el-tabs__item.is-active {
  color: white !important;
  background-color: #5a55ce !important;
}
</style>

<style scoped>
.assign-legend {
  padding: 0 6px;
  font-size: 11px;
  font-weight: 600;
  line-height: 1;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--el-text-color-secondary);
}
</style>
